import { TIME_INTERFACE_PERMISSIONS, WidgetItem } from '@vyce/core/src/types';

/**
 * Which right a Time dashboard widget needs, keyed by `system_name`. Any-of: one
 * right from the list is enough.
 *
 * The right is the one the widget's own endpoint checks — see the request each
 * widget makes in `api/time/index.ts`. Entries marked TODO(catalogue) are
 * inferred from the endpoint name and not yet confirmed against the permission
 * catalogue; a wrong guess there costs nothing, because a 403 from the widget's
 * request hides it anyway (see useWidgetErrorHandler).
 */
export const TIME_WIDGET_PERMISSIONS: Record<string, string[]> = {
  // monitoring/{company}/map_view
  team_on_site: [TIME_INTERFACE_PERMISSIONS.READ_MAP_VIEW],
  // monitoring/{company}/workers/checked_in
  clocked_in_now: [TIME_INTERFACE_PERMISSIONS.SEARCH_CHECKED_IN_WORKERS],
  // monitoring/{company}/workers/checked_out — TODO(catalogue): may be its own right
  clocked_out_today: [TIME_INTERFACE_PERMISSIONS.SEARCH_CHECKED_IN_WORKERS],
  // monitoring/{company}/workers/did_not_clock_out — TODO(catalogue)
  not_clocked_out_widget: [TIME_INTERFACE_PERMISSIONS.SEARCH_CHECKED_IN_WORKERS],
  // monitoring/{company}/location/workers — TODO(catalogue)
  location_users: [TIME_INTERFACE_PERMISSIONS.USERS],
  // monitoring/{company}/workers/dormant — TODO(catalogue)
  dormant_team_members: [TIME_INTERFACE_PERMISSIONS.USERS],
  // monitoring/{company}/workers/no_rate — TODO(catalogue)
  team_members_missing_rate: [TIME_INTERFACE_PERMISSIONS.USERS],
  // monitoring/{company}/recently_registered_time_users — TODO(catalogue)
  recently_registered_time_users: [TIME_INTERFACE_PERMISSIONS.USERS],
  // time_logs/{company}/widgets/top_performing_workers_last_week
  top_performing_team_members_last_week: [TIME_INTERFACE_PERMISSIONS.READ_TIME_LOGS],
  // time_logs/{company}/widgets/workers_worked_time_overview
  workers_worked_time_overview: [TIME_INTERFACE_PERMISSIONS.READ_TIME_LOGS],
  // time_logs/{company}/widgets/on_site_trades
  on_site_trades: [TIME_INTERFACE_PERMISSIONS.READ_TIME_LOGS],
};

/**
 * Anything not in the table — the generic registry widgets that post to their own
 * `widget.url` — is a time-logs statistic, so it follows that right.
 */
export const TIME_WIDGET_DEFAULT_PERMISSIONS = [TIME_INTERFACE_PERMISSIONS.READ_TIME_LOGS];

/**
 * Drops the widgets this user may not see. `canAnywhere`, not `can`: every Time
 * right can be granted on a single location, and the widgets filter their own
 * data by location.
 */
export const filterWidgetsByPermission = (
  widgets: WidgetItem[],
  permissionsByWidget: Record<string, string[]>,
  defaultPermissions: string[],
  canAnywhere: (permission: string) => boolean
): WidgetItem[] =>
  widgets.filter(widget => {
    const required = permissionsByWidget[widget.system_name] || defaultPermissions;
    return !required.length || required.some(permission => canAnywhere(permission));
  });
